import { forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { usePopup } from '@/modules/Popup/hooks/usePopup';
import { ColorPickerContent } from '../molecules';
import { ColorInput } from '../atoms';
import { useRecentColors } from '../../hooks';
import { useControlledPrimitive } from '@/shared/hooks/useControlledState';
import { ColorPickerProps, ColorPickerRef } from '@/shared/types/colorpicker';
import { normalizeHexColor, isValidHexColor } from '../../utils';
import { cn } from '@/shared/utils/cn';

export const ColorPicker = forwardRef<ColorPickerRef, ColorPickerProps>(
  (
    {
      value,
      defaultValue = '',
      onColorChange,
      maxRecentColors = 10,
      initialRecentColors = [],
      closeOnSelect = false,
      className,
      inputProps = {},
      popupProps = {},
      label,
      error,
      helperText,
      disabled = false,
      position = 'bottom-left',
      showHexInput = true,
    },
    ref,
  ) => {
    // Hook para manejar el popup
    const {
      isOpen,
      popupStyle,
      isPositioned,
      triggerRef,
      popupRef,
      containerRef,
      togglePopup,
      closePopup,
    } = usePopup({
      position,
      offset: popupProps.offset || 6,
      closeOnClickOutside: popupProps.closeOnClickOutside !== false,
      closeOnEscape: popupProps.closeOnEscape !== false,
      popupId: popupProps.popupId,
      onOpen: popupProps.onOpen,
      onClose: popupProps.onClose,
      disabled,
      absolute: popupProps.absolute || false,
    });

    // Manejar estado controlado del color
    const { value: currentColor, setValue: setCurrentColor } = useControlledPrimitive({
      value: value ? normalizeHexColor(value) : undefined,
      defaultValue: defaultValue ? normalizeHexColor(defaultValue) : '',
      onChange: (newColor) => {
        const normalized = normalizeHexColor(newColor);
        if (isValidHexColor(normalized)) {
          onColorChange?.(normalized);
        }
      },
    });

    // Hook para manejar colores recientes
    const { recentColors, addRecentColor, addRecentColorImmediate } = useRecentColors({
      maxRecentColors,
      initialRecentColors,
    });

    // Manejar selección de color (con debounce para el guardado)
    const handleColorSelect = (color: string) => {
      const normalized = normalizeHexColor(color);
      if (!isValidHexColor(normalized)) return;

      setCurrentColor(normalized);
      // Usar debounce para no guardar constantemente durante el arrastre
      addRecentColor(normalized);

      // Cerrar popup si está habilitado
      if (closeOnSelect) {
        closePopup();
      }
    };

    // Exponer métodos a través de ref
    useImperativeHandle(
      ref,
      () => ({
        getValue: () => currentColor || '',
        setValue: (color: string) => {
          const normalized = normalizeHexColor(color);
          if (isValidHexColor(normalized)) {
            setCurrentColor(normalized);
          }
        },
        open: () => {
          if (!disabled) {
            togglePopup();
          }
        },
        close: () => {
          closePopup();
        },
        isOpen: () => isOpen,
      }),
      [currentColor, disabled, setCurrentColor, togglePopup, closePopup, isOpen],
    );

    // Manejar click en el input para abrir el popup
    const handleInputClick = (e: React.MouseEvent) => {
      if (!disabled) {
        e.stopPropagation();
        togglePopup();
      }
    };

    // Manejar cambio manual en el input
    const handleInputChange = (newValue: string) => {
      const normalized = normalizeHexColor(newValue);
      if (isValidHexColor(normalized)) {
        setCurrentColor(normalized);
        // Para cambios manuales en el input, guardar inmediatamente (sin debounce)
        addRecentColorImmediate(normalized);
      } else if (newValue === '' || newValue === '#') {
        // Permitir borrar o escribir solo #
        setCurrentColor(newValue);
      }
    };

    // Color normalizado para mostrar en el input
    const displayColor = useMemo(() => {
      if (!currentColor) return '';
      const normalized = normalizeHexColor(currentColor);
      return isValidHexColor(normalized) ? normalized : currentColor;
    }, [currentColor]);

    // Renderizar el popup
    const renderPopup = () => {
      if (!isOpen) return null;

      const positionValue = popupProps.absolute ? ('absolute' as const) : ('fixed' as const);
      const visibilityValue = isPositioned ? ('visible' as const) : ('hidden' as const);

      const initialStyle: React.CSSProperties = {
        position: positionValue,
        top: popupProps.absolute ? 'auto' : '-9999px',
        left: popupProps.absolute ? 'auto' : '-9999px',
        visibility: visibilityValue,
        opacity: isPositioned ? 1 : 0,
        transition: 'opacity 0.1s ease-out',
        ...popupStyle,
      };

      const popupContent = (
        <div
          ref={popupRef}
          style={initialStyle}
          className={cn(
            'ui-overflow-hidden ui-rounded-surface-card ui-border-1 ui-border-solid ui-border-default ui-bg-surface-default ui-shadow-lg',
            popupProps.className,
          )}
          onClick={(e) => {
            if (popupProps.stopPropagationList !== false) {
              e.stopPropagation();
            }
            if (popupProps.closeOnClick !== false) {
              // No cerrar automáticamente, solo cuando se selecciona un color
            }
          }}
        >
          <ColorPickerContent
            currentColor={displayColor}
            recentColors={recentColors}
            onColorSelect={handleColorSelect}
            showHexInput={showHexInput}
          />
        </div>
      );

      return popupProps.absolute ? popupContent : createPortal(popupContent, document.body);
    };

    return (
      <div ref={containerRef} className={cn('ui-imj', 'ui-relative', className)}>
        <div
          ref={triggerRef}
          onClick={handleInputClick}
          className={disabled ? 'ui-cursor-not-allowed' : 'ui-cursor-pointer'}
        >
          <ColorInput
            color={displayColor}
            label={label}
            error={error}
            helperText={helperText}
            disabled={disabled}
            onColorChange={handleInputChange}
            onClick={() => handleInputClick({} as React.MouseEvent)}
            className={cn('ui-w-full', inputProps.className)}
          />
        </div>
        {renderPopup()}
      </div>
    );
  },
);

ColorPicker.displayName = 'ColorPicker';
